import { formatIBAN, useAppSelector, useAppTranslation } from "@/utils";
import { BankSaman, CheckCircle, CuToman, FiAlertOctagon, FiAlertTriangle, FiLoader } from "@payping/icons";
import { Button, Divider, Flex, LegacyCard, Space, Tag, Typography, usePaypingContext } from "@payping/web-components";
import { Link, generatePath, useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import { AgreementStateEnum } from "@payping/api";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { ctaId, offlineReportId } from "@/assets/googleTrackIds";
import { CreateSamanStepEnum } from "@/pages/DigitalAccount/Saman/Create";
import Invoice from "@/pages/DigitalAccount/Saman/invoice";
import { useState } from "react";

const SamanCard = () => {
  const { breakpoints } = usePaypingContext();
  const { sm } = breakpoints;
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { bankAgreement } = useAppSelector((state) => state.digitalAccount);
  const { bankAccount } = useAppSelector((state) => state.digitalAccount);
  const { balance } = useAppSelector((state) => state.digitalAccount.saman.depositBalance);
  const state = bankAgreement.find((value) => value.agreementInfo)?.agreementInfo?.state;
  const { iban, ownerName } = bankAccount.find((value) => value.iban) || {};
  const [openModal, setOpenModal] = useState(false);

  const Icon = () => {
    const commonProps = {
      width: 20,
      strokeWidth: 1.5,
    };

    if (bankAccount.length) {
      return <CheckCircle className="color--success" {...commonProps} />;
    }
    if (state === AgreementStateEnum.Created) {
      return <FiAlertTriangle className="color--warning" {...commonProps} />;
    }
    if (state === AgreementStateEnum.AcceptedByUser) {
      return <FiLoader className="color--warning" {...commonProps} />;
    }
    if (state === AgreementStateEnum.Confirmed) {
      return <CheckCircle className="color--success" {...commonProps} />;
    }
    if (state === AgreementStateEnum.Rejecetd) {
      return <FiAlertOctagon className="color--danger" {...commonProps} />;
    }
  };

  const CardDesc = () => {
    if (bankAccount.length) {
      return (
        <Flex vertical gap={8}>
          <Typography.Text type={"secondary"}>{t("balance")}</Typography.Text>
          <Flex align="center">
            <Typography.Text
              strong
            >{`${formatLocalizedNumber({ value: Math.trunc((balance || 0) / 10) })}`}</Typography.Text>
            <CuToman width={24} height={24} />
          </Flex>
        </Flex>
      );
    }
    if (state === AgreementStateEnum.Created) {
      return (
        <Flex vertical gap={8}>
          <Typography.Text type={"secondary"}>{t("digitalAccount.status.createdDesc")}</Typography.Text>
          <Typography.Text>&nbsp;</Typography.Text>
        </Flex>
      );
    }
    if (state === AgreementStateEnum.AcceptedByUser) {
      return (
        <Flex vertical gap={8}>
          <Typography.Text type={"secondary"}>{t("digitalAccount.status.acceptedByUserDesc")}</Typography.Text>
          <Typography.Text>&nbsp;</Typography.Text>
        </Flex>
      );
    }
    if (state === AgreementStateEnum.Confirmed) {
      return (
        <Flex vertical gap={8}>
          <Typography.Text type={"secondary"}>{t("digitalAccount.status.confirmedDesc")}</Typography.Text>
          <Typography.Text>&nbsp;</Typography.Text>
        </Flex>
      );
    }
    if (state === AgreementStateEnum.Rejecetd) {
      return (
        <Flex vertical gap={8}>
          <Typography.Text type={"secondary"}>{t("digitalAccount.status.rejecetdDesc")}</Typography.Text>
          <Typography.Text>&nbsp;</Typography.Text>
        </Flex>
      );
    }
  };

  const CardBtn = () => {
    if (bankAccount.length) {
      return (
        <Flex gap={8}>
          <Button
            type={"primary"}
            data-google-track={ctaId}
            onClick={() =>
              navigate(
                generatePath(DashboardRoutes.digitalAccount.saman.create.url, {
                  step: String(CreateSamanStepEnum.Configuration),
                }),
              )
            }
          >
            {t("digitalAccount.yourAccount.bankTransfer")}
          </Button>
          <Invoice openModal={openModal} setOpenModal={setOpenModal} />
          <Button onClick={() => setOpenModal(true)} type={"link"} data-google-track={offlineReportId}>
            {t("digitalAccount.yourAccount.invoice")}
          </Button>
        </Flex>
      );
    }
    if (state === AgreementStateEnum.Created) {
      return (
        <Link to={DashboardRoutes.digitalAccount.agreement?.url}>
          <Button type="default">{t("digitalAccount.yourAccount.continueOpeningAccount")}</Button>
        </Link>
      );
    }
    if (state === AgreementStateEnum.AcceptedByUser) {
      return (
        <Button disabled type="default">
          {t("common.awaitingConfirmation")}
        </Button>
      );
    }
    if (state === AgreementStateEnum.Confirmed) {
      return (
        <Link to={DashboardRoutes.digitalAccount.saman.accountConnection?.url}>
          <Button type="default">{t("digitalAccount.yourAccount.continueOpeningAccount")}</Button>
        </Link>
      );
    }
    if (state === AgreementStateEnum.Rejecetd) {
      return (
        <Link to={DashboardRoutes.digitalAccount.agreement?.url}>
          <Button type="primary">{t("digitalAccount.status.modificationOfContract")}</Button>
        </Link>
      );
    }
  };

  return (
    <LegacyCard.Ant
      cover={
        <div className={sm ? "background--gray100 flex justify-center p-2" : ""}>
          <div
            className="tw:relative tw:h-[199px]"
            style={{
              background: "#0F6B99",
              ...(sm ? { width: "370px", borderRadius: "4px" } : {}),
              ...(!bankAccount.length
                ? {
                    background:
                      "linear-gradient(rgba(162, 91, 219, 1),rgba(104, 59, 142, 1),rgba(104, 59, 142, 1),rgba(206, 119, 174, 1))",
                    mixBlendMode: "luminosity",
                  }
                : {}),
            }}
          >
            <div className={"tw:flex tw:flex-col! tw:pl-5! tw:pr-5! tw:pt-8!"}>
              <Space className={"tw:mb-4! tw:flex! tw:justify-between!"}>
                <Typography.Text strong className={"color--white"}>
                  {state !== AgreementStateEnum.Confirmed ? t("unclear") : ownerName}
                </Typography.Text>
                <BankSaman width={32} />
              </Space>
              <div className={"tw:flex!"}>
                <Typography.Text className={"color--white"}>{t("profile.shaba")}</Typography.Text>
              </div>
            </div>
            <div
              className="tw:absolute tw:bottom-8 tw:h-[38px] tw:w-full"
              style={{
                background: "rgba(0, 0, 0, 0.68)",
              }}
            >
              <Typography.Text
                style={!sm ? { width: 350 } : { width: "100%" }}
                className={"color--white tw:flex! tw:h-full tw:items-center! tw:justify-end! tw:pl-8!"}
              >
                <Space size={"middle"} className={"ltr"}>
                  {formatIBAN(iban || "IR------------------------")
                    ?.split(" ")
                    ?.map((code, index) => (
                      <Typography.Text key={index} className={"color--white en-font"}>
                        {code}
                      </Typography.Text>
                    ))}
                </Space>
              </Typography.Text>
            </div>
          </div>
        </div>
      }
    >
      <Flex vertical gap={16}>
        <Flex gap={8}>
          {Icon()}
          <div className="tw:flex! tw:w-full tw:justify-between!">
            <Typography.Title level={4} className={"tw:mb-0!"}>
              {t("digitalAccount.yourAccount.samanAccount")}
            </Typography.Title>
            {state === AgreementStateEnum.Created ? (
              <div>
                <Tag color={"warning"}>{t("digitalAccount.yourAccount.incompleteInformation")}</Tag>
              </div>
            ) : null}
            {state === AgreementStateEnum.Confirmed ? (
              <div>
                <Tag color={"green"}>{t("verified")}</Tag>
              </div>
            ) : null}
          </div>
        </Flex>
        <Divider className={"tw:my-0!"} />
        {CardDesc()}
        {CardBtn()}
      </Flex>
    </LegacyCard.Ant>
  );
};

export default SamanCard;
